import React, { PureComponent } from 'react';
import { Loader } from 'react-loaders';
import _ from "lodash";
import moment from "moment-timezone";

import {
  batchRequest,
  putRequest,
  postRequest
} from '../../../../api/ApiService';
import { getDateInCurrentTimezone } from "../../../../services/utils";

import {
  Wrapper,
  Title,
  Text,
  DataContainer,
  DataContainerTitle,
  FourColumnsRow,
  DataColumn,
  DataTitle,
  ButtonRow,
  Button,
  HeaderColumn,
  TableCell,
  NAItem
} from '../components/ui';
import InputPercentage from '../components/input-percentage';

import AbbreviateNumber from "../../../../components/ui-building-blocks/numbers/AbbreviateNumber";
import ErrorAlert from '../../../../components/alerts/error';
import SuccessAlert from '../../../../components/alerts/success';

class TeamScore extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      roundTargets: [],
      changedRoundTargets: [],
      roundsWeights: [],
      roundsWeightsValueChanged: false,
      showError: false,
      errorTitle: '',
      errorMessage: '',
      showSuccess: false,
      successMessage: '',
      defaultActiveRoundTargets: [],
      isDefaultRoundsActive: false
    };
  }

  componentDidMount() {
    batchRequest([
      `${process.env.REACT_APP_API_URL}v2/games/targets/`,
      `${process.env.REACT_APP_API_URL}game/rounds/`,
      `${process.env.REACT_APP_API_URL}courses/`,
    ])
      .then(res => {
        const gamesTargets = res[0].body;
        const rounds = res[1].body;
        const courseId = res[2].body[0].id;

        const defaultTargetsData = [
          {
            id: 1,
            course: courseId,
            roundNumber: 1,
            marketShare: 12,
            netRevenues: '4.00',
            grossMargin: 60,
            stockPrice: 1.65,
            roe: 0,
            roa: 0,
            eps: 0,
            roundEndDate: null
          },
          {
            id: 2,
            course: courseId,
            roundNumber: 2,
            marketShare: 15,
            netRevenues: '10.00',
            grossMargin: 55,
            stockPrice: 3,
            roe: 0,
            roa: 0,
            eps: 0,
            roundEndDate: null
          },
          {
            id: 3,
            course: courseId,
            roundNumber: 3,
            marketShare: 0,
            netRevenues: '17.50',
            grossMargin: 55,
            stockPrice: 0,
            roe: 25,
            roa: 25,
            eps: 0,
            roundEndDate: null
          },
        ];

        const isDefaultRoundTargetsActive = gamesTargets.length === 0;
        const isDefaultRoundsActive = rounds.length === 0;

        const roundTargets = isDefaultRoundTargetsActive
          ? defaultTargetsData
          : gamesTargets.map((gamesTarget) => {
            const marketShare = gamesTarget.market_share ? (gamesTarget.market_share * 100).toFixed(2) : 0;
            const netRevenues = gamesTarget.net_revenues ? (gamesTarget.net_revenues / 1000000).toFixed(2) : 0;
            const grossMargin = gamesTarget.gross_margin ? (gamesTarget.gross_margin * 100).toFixed(2) : 0;
            const stockPrice = gamesTarget.stock_price ? (gamesTarget.stock_price).toFixed(2) : 0;
            const roe = gamesTarget.roe ? (gamesTarget.roe * 100).toFixed(2) : 0;
            const roa = gamesTarget.roa ? (gamesTarget.roa * 100).toFixed(2) : 0;
            const eps = gamesTarget.eps ? (gamesTarget.eps).toFixed(2) : 0;

            const relativeRound = rounds ? rounds.find(round => round.number === gamesTarget.round_number) : null;

            return {
              id: gamesTarget.id,
              course: gamesTarget.course,
              roundNumber: gamesTarget.round_number,
              marketShare,
              netRevenues,
              grossMargin,
              stockPrice,
              roe,
              roa,
              eps,
              roundEndDate: (rounds && relativeRound) ? relativeRound.end_date : null
            }
          });

        const roundsWeights = isDefaultRoundsActive
          ? Array.from({ length: 3 }, (_, i) => ({
            id: i + 1,
            number: i + 1,
            gradingWeight: i === 0 ? 20 : 40,
            startDate: moment(),
            endDate: moment().add(1, 'days'),
            game: 0
          }))
          : rounds.map((round) => (
            {
              id: round.id,
              number: round.number,
              gradingWeight: (round.grading_weight * 100).toFixed(2),
              startDate: round.start_date,
              endDate: round.end_date,
              game: round.game
            }
          ));


        const roundTargetsFiltered = isDefaultRoundsActive
          ? roundTargets
          : roundTargets.filter(roundTarget => {
            return (
              roundsWeights.some(round => round.number === roundTarget.roundNumber)
            )
          });

        this.setState({
          roundTargets: roundTargetsFiltered,
          roundsWeights,
          loaded: true,
          defaultActiveRoundTargets: isDefaultRoundTargetsActive ? [1, 2, 3] : [],
          isDefaultRoundsActive
        });
      })
      .catch(err => { });
  }

  handleRoundsTargetsInputChange = (e, value, roundNumber, name) => {
    const val = 'floatValue' in value ? value.floatValue : Number(e.target.value);
    let roundTargets = _.cloneDeep(this.state.roundTargets);
    const index = roundTargets.findIndex(i => i.roundNumber === Number(roundNumber));

    if (index >= 0) {
      roundTargets[index][name] = val.toFixed(2);
      let changedRoundTargets = [...this.state.changedRoundTargets];
      if (!changedRoundTargets.includes(roundNumber)) {
        changedRoundTargets.push(roundNumber);
      }

      this.setState({
        roundTargets,
        changedRoundTargets,
      });
    }
  }

  handleRoundsWeightsInputChange = (name, value) => {
    let roundsWeights = _.cloneDeep(this.state.roundsWeights);
    const index = roundsWeights.findIndex(i => i.number === Number(name));

    if (index >= 0) {
      roundsWeights[index].gradingWeight = value;

      this.setState({
        roundsWeights,
        roundsWeightsValueChanged: true
      });
    }
  }

  submitRoundTarget = (roundTarget) => {
    const { changedRoundTargets, defaultActiveRoundTargets } = this.state;

    const apiUrl = `${process.env.REACT_APP_API_URL}v2/games/targets/`;
    const request = defaultActiveRoundTargets.includes(roundTarget.roundNumber) ? postRequest(`${apiUrl}?round_number=${roundTarget.roundNumber}/`) : putRequest(`${apiUrl}${roundTarget.id}/?round_number=${roundTarget.roundNumber}/`);

    request.send({
      course: roundTarget.course,
      round_number: roundTarget.roundNumber,
      market_share: roundTarget.marketShare / 100,
      net_revenues: roundTarget.netRevenues * 1000000,
      gross_margin: roundTarget.grossMargin / 100,
      stock_price: roundTarget.stockPrice,
      roe: roundTarget.roe / 100,
      roa: roundTarget.roa / 100,
      earnings_per_share: roundTarget.eps
    })
      .then(() => {
        this.setState({
          showSuccess: true,
          successMessage: `Round ${roundTarget.roundNumber} targets are updated.`,
          changedRoundTargets: changedRoundTargets.filter(i => i !== roundTarget.roundNumber),
          defaultActiveRoundTargets: defaultActiveRoundTargets.filter(i => i !== roundTarget.roundNumber),
        });
      })
      .catch(() => { });
  }

  submitRoundWeights = () => {
    const { roundsWeights, isDefaultRoundsActive } = this.state;

    if (isDefaultRoundsActive) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Rounds not set up',
        errorMessage: 'You’ll need to activate rounds before you can set round weights. Please navigate to the Round page within Game Admin'
      });
      return;
    }

    const sum = roundsWeights.reduce((partialSum, round) => partialSum + Number(round.gradingWeight), 0);
    if (sum !== 100) {
      this.setState({
        showError: true,
        errorTitle: 'Error | Must equal 100%',
        errorMessage: 'The sum of all the round weights do not equal 100%. Please modify to ensure that its 100%.'
      });
      return;
    }

    let wasErrorDuringUpdating = false;

    roundsWeights.forEach(round => {
      putRequest(
        `${process.env.REACT_APP_API_URL}game/rounds/${round.id}/`
      ).send({
        grading_weight: round.gradingWeight / 100,
        number: round.number,
        game: round.game,
        end_date: round.endDate,
        start_date: round.startDate
      })
        .then(() => {
        })
        .catch(() => { wasErrorDuringUpdating = true; });
    });

    let showSuccess = false;
    let successMessage = '';
    if (!wasErrorDuringUpdating) {
      showSuccess = true;
      successMessage = 'The Round weights are updated.';
    }

    this.setState({
      showSuccess,
      successMessage,
      roundsWeightsValueChanged: false
    });
  }

  hideError = () => {
    this.setState({
      showError: false
    });
  };

  render() {
    const {
      loaded,
      roundTargets,
      changedRoundTargets,
      roundsWeights,
      roundsWeightsValueChanged,
      showError,
      errorTitle,
      errorMessage,
      showSuccess,
      successMessage,
      defaultActiveRoundTargets,
      isDefaultRoundsActive
    } = this.state;

    const roundWeightsButtonText = isDefaultRoundsActive
      ? 'Activate'
      : roundsWeightsValueChanged
        ? 'Update'
        : 'Updated';

    return loaded ? (
      <Wrapper>
        {showError && (
          <ErrorAlert
            title={errorTitle}
            message={errorMessage}
            onHide={this.hideError}
          />
        )}
        <Title>TEAM SCORE</Title>
        <Text>The team score is driven by how well a team is able to hit the financial targets each round (i.e. Round Targets).  If they hit the targets, they receive a round score of 100%. The team score is a weighted average value of all of the round scores.</Text>
        <br />
        <Text>Note that a few financial metrics are only relevant in certain years. </Text>
        {showSuccess && (
          <SuccessAlert
            title="Success!"
            message={successMessage}
            onHide={this.hideError}
          />
        )}
        <DataContainer>
          <DataContainerTitle>Modify Round Targets</DataContainerTitle>
          <table>
            <thead>
              <tr>
                <HeaderColumn>Round</HeaderColumn>
                <HeaderColumn>Market<br /> Share</HeaderColumn>
                <HeaderColumn>Revenues</HeaderColumn>
                <HeaderColumn>Gross<br /> Margins</HeaderColumn>
                <HeaderColumn>Share<br /> Price</HeaderColumn>
                <HeaderColumn>ROE</HeaderColumn>
                <HeaderColumn>ROA</HeaderColumn>
                <HeaderColumn>EPS</HeaderColumn>
              </tr>
            </thead>
            <tbody>
              {
                roundTargets.map((roundTargets, index) => {
                  const isDefault = defaultActiveRoundTargets.includes(roundTargets.roundNumber);
                  const roundTargetsButtonActive = isDefault || changedRoundTargets.includes(roundTargets.roundNumber) ? true : false;
                  const roundTargetsButtonText = isDefault
                    ? 'Activate'
                    : changedRoundTargets.includes(roundTargets.roundNumber)
                      ? 'Update'
                      : 'Updated';

                  const endDate = getDateInCurrentTimezone(
                    moment(roundTargets.roundEndDate)
                  );
                  const nowDate = getDateInCurrentTimezone(moment());
                  const inputDisabled = nowDate > endDate;

                  return (
                    <tr key={index}>
                      <TableCell>{roundTargets.roundNumber}</TableCell>
                      <TableCell>
                        {
                          roundTargets.marketShare !== 0 &&
                          <AbbreviateNumber
                            type="input"
                            value={roundTargets.marketShare}
                            onChange={(e, value) =>
                              this.handleRoundsTargetsInputChange(
                                e,
                                value,
                                roundTargets.roundNumber,
                                'marketShare'
                              )
                            }
                            prefix=""
                            suffix="%"
                            disabled={inputDisabled}
                          />
                        }
                        {
                          roundTargets.marketShare === 0 &&
                          <NAItem>N/A</NAItem>
                        }
                      </TableCell>
                      <TableCell>
                        {
                          roundTargets.netRevenues !== 0 &&
                          <AbbreviateNumber
                            type="input"
                            value={roundTargets.netRevenues}
                            onChange={(e, value) =>
                              this.handleRoundsTargetsInputChange(
                                e,
                                value,
                                roundTargets.roundNumber,
                                'netRevenues'
                              )
                            }
                            suffix="M"
                            disabled={inputDisabled}
                          />
                        }
                        {
                          roundTargets.netRevenues === 0 &&
                          <NAItem>N/A</NAItem>
                        }
                      </TableCell>
                      <TableCell>
                        {
                          roundTargets.grossMargin !== 0 &&
                          <AbbreviateNumber
                            type="input"
                            value={roundTargets.grossMargin}
                            onChange={(e, value) =>
                              this.handleRoundsTargetsInputChange(
                                e,
                                value,
                                roundTargets.roundNumber,
                                'grossMargin'
                              )
                            }
                            prefix=""
                            suffix="%"
                            disabled={inputDisabled}
                          />
                        }
                        {
                          roundTargets.grossMargin === 0 &&
                          <NAItem>N/A</NAItem>
                        }
                      </TableCell>
                      <TableCell>
                        {
                          roundTargets.stockPrice !== 0 &&
                          <AbbreviateNumber
                            type="input"
                            value={roundTargets.stockPrice}
                            onChange={(e, value) =>
                              this.handleRoundsTargetsInputChange(
                                e,
                                value,
                                roundTargets.roundNumber,
                                'stockPrice'
                              )
                            }
                            disabled={inputDisabled}
                          />
                        }
                        {
                          roundTargets.stockPrice === 0 &&
                          <NAItem>N/A</NAItem>
                        }
                      </TableCell>
                      <TableCell>
                        {
                          roundTargets.roe !== 0 &&
                          <AbbreviateNumber
                            type="input"
                            value={roundTargets.roe}
                            onChange={(e, value) =>
                              this.handleRoundsTargetsInputChange(
                                e,
                                value,
                                roundTargets.roundNumber,
                                'roe'
                              )
                            }
                            prefix=""
                            suffix="%"
                            disabled={inputDisabled}
                          />
                        }
                        {
                          roundTargets.roe === 0 &&
                          <NAItem>N/A</NAItem>
                        }
                      </TableCell>
                      <TableCell>
                        {
                          roundTargets.roa !== 0 &&
                          <AbbreviateNumber
                            type="input"
                            value={roundTargets.roa}
                            onChange={(e, value) =>
                              this.handleRoundsTargetsInputChange(
                                e,
                                value,
                                roundTargets.roundNumber,
                                'roa'
                              )
                            }
                            prefix=""
                            suffix="%"
                            disabled={inputDisabled}
                          />
                        }
                        {
                          roundTargets.roa === 0 &&
                          <NAItem>N/A</NAItem>
                        }
                      </TableCell>
                      <TableCell>
                        {
                          roundTargets.eps !== 0 &&
                          <AbbreviateNumber
                            type="input"
                            value={roundTargets.eps}
                            onChange={(e, value) =>
                              this.handleRoundsTargetsInputChange(
                                e,
                                value,
                                roundTargets.roundNumber,
                                'eps'
                              )
                            }
                            prefix=""
                            suffix="%"
                            disabled={inputDisabled}
                          />
                        }
                        {
                          roundTargets.eps === 0 &&
                          <NAItem>N/A</NAItem>
                        }
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          disabled={!roundTargetsButtonActive}
                          isActiveDefault={isDefault}
                          onClick={() => this.submitRoundTarget(roundTargets)}
                        >
                          {roundTargetsButtonText}
                        </Button>
                      </TableCell>
                    </tr>
                  )
                })
              }
            </tbody>
          </table>
        </DataContainer>
        <DataContainer>
          <DataContainerTitle>Modify Round Weights</DataContainerTitle>
          <FourColumnsRow>
            {
              roundsWeights.map((round, index) => {
                const endDate = getDateInCurrentTimezone(
                  moment(round.endDate)
                );
                const nowDate = getDateInCurrentTimezone(moment());
                const inputDisabled = nowDate > endDate;

                return (
                  <DataColumn key={index}>
                    <DataTitle>{`Round ${round.number}`}</DataTitle>
                    <InputPercentage
                      value={round.gradingWeight}
                      name={round.number}
                      onInputChange={this.handleRoundsWeightsInputChange}
                      disabled={inputDisabled}
                    />
                  </DataColumn>
                )
              })
            }
          </FourColumnsRow>
          <ButtonRow>
            <Button
              type="button"
              onClick={this.submitRoundWeights}
              disabled={!(roundsWeightsValueChanged || isDefaultRoundsActive)}
              isActiveDefault={isDefaultRoundsActive}
            >
              {roundWeightsButtonText}
            </Button>
          </ButtonRow>
        </DataContainer>
      </Wrapper>
    ) : <Wrapper><Loader type="ball-scale-ripple" active /></Wrapper>;
  }

}

export default TeamScore;



// WEBPACK FOOTER //
// ./src/instructor/components/grading/pages/team-score.js